import moment from 'moment';
import type { LargeShiftDetailsProps } from '@tempmee/react-blocks';
import type { TCompletedShiftJob } from '../../CompletedShiftDetailsPage/utils/completedShiftView';
import { toShiftSummaryFields } from '../../shared/utils/shiftSummaryProps';

/**
 * A scheduled shift as these mappers need it.
 *
 * Structurally the same widening the COMPLETED page needs — `procedures`,
 * `autofillOptions` and `counterCover` are all on the payload but not on `IJob`
 * — so the alias is shared rather than redeclared. The two states read the same
 * `/jobs/:id` document; only the lifecycle differs.
 */
export type TScheduledShiftJob = TCompletedShiftJob;

/** The `LargeShiftDetails` props this page derives; the rest are block defaults. */
export type TScheduledShiftSummary = Pick<
  LargeShiftDetailsProps,
  'status' | 'date' | 'startTime' | 'endTime' | 'totalHours' | 'position' | 'rate'
>;

/**
 * Maps a scheduled shift onto the `LargeShiftDetails` summary.
 *
 * Only `status` is decided here: the gate routes `JobStatusEnum.Filled` and
 * nothing else to this page, so the lifecycle state is fixed rather than derived
 * from a local status table — `SHIFT_STATUS_META` in `@tempmee/core` owns the
 * label and the colour that `'scheduled'` resolves to.
 *
 * Every other field comes from the shared `toShiftSummaryFields`, which the
 * Confirm Candidates page already reads: the date, times, duration, position and
 * rate of a shift do not depend on which state page is rendering them, and a
 * second copy of that arithmetic is how two pages start disagreeing about the
 * same shift.
 */
export const toScheduledShiftSummary = (job: TScheduledShiftJob): TScheduledShiftSummary => ({
  ...toShiftSummaryFields(job),
  status: 'scheduled',
});


/**
 * Whether the office can still message the professional working this shift —
 * the legacy `chatBtnDisplay` gate, minus its status check.
 *
 * The status check is dropped deliberately: the gate only routes a `filled` shift
 * here, so re-testing it locally would add a second source of truth that can
 * disagree with the router. (The legacy reads `job_status` while the gate reads
 * `status`; the payload carries both, and a page that gated on the other one
 * would silently hide its own controls if they ever diverged.)
 *
 * The window closes when the shift *ends*, not when it starts — there is still a
 * professional to reach mid-shift.
 */
export const canMessageProfessional = (job: TScheduledShiftJob, now: Date = new Date()): boolean =>
  !job.is_cancelled &&
  Boolean(job.offer_final_reciever) &&
  moment(job.job_datetime_end).toDate() > now;

/**
 * Whether the shift can still be cancelled — the legacy `cancelBtnDisplay` gate,
 * with the same status check dropped for the same reason.
 *
 * This window closes at the shift's *start*: once it is under way there is
 * nothing left to call off, which is why it is a tighter bound than
 * {@link canMessageProfessional}.
 */
export const canCancelShift = (job: TScheduledShiftJob, now: Date = new Date()): boolean =>
  !job.is_cancelled && moment(job.job_datetime).toDate() > now;

/**
 * Whether the shift has a transaction history to link to.
 *
 * Re-exported from the COMPLETED page rather than reimplemented: the legacy
 * `transactionHistoryBtn` is one gate covering `filled` alongside the terminal
 * statuses, and the link it guards goes to the same route from either page.
 */
